import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWandMagicSparkles, faPaperPlane, faMicrophone, faStop,
    faBoxOpen, faCheck, faCircleCheck, faArrowRight, faTriangleExclamation,
    faVolumeHigh, faPhoneSlash, faXmark, faPenToSquare,
    faBoxesStacked, faChartLine, faHourglassHalf, faUserClock, faCalendarDay, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import useVoiceConversation, { UNSUPPORTED_VOICE_MESSAGE } from './useVoiceConversation';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import { eventBus } from '../../bus/eventBus';
import { API_BASE_URL } from '../../api';
import { useAuth } from '../../context/AuthProvider';
import { isAssistantEnabled } from './assistantSettings';

import './Assistant.scss';

const WHISPER_API_URL = process.env.REACT_APP_WHISPER_API_URL || 'http://127.0.0.1:5000';
const MAX_HISTORY = 10;

// Welcome screen suggestions: `prompt` is sent as is; `prefill` only fills the field (the
// pharmacist completes it — e.g. the product's name and price).
const SUGGESTIONS = [
    { icon: faBoxesStacked, label: "Stock d'un produit", prompt: 'Quel est le stock du Doliprane 500 mg ?', color: '#1e40af', bg: '#eff6ff' },
    { icon: faChartLine, label: 'Activité du jour', prompt: "Résume l'activité de la pharmacie aujourd'hui.", color: '#1e40af', bg: '#eff6ff' },
    { icon: faHourglassHalf, label: 'Péremptions', prompt: 'Quels produits vont périmer dans les 30 prochains jours ?', color: '#1e40af', bg: '#eff6ff' },
    { icon: faUserClock, label: 'Patients impayés', prompt: 'Quels patients ont un solde impayé ?', color: '#1e40af', bg: '#eff6ff' },
    { icon: faCalendarDay, label: 'Rendez-vous', prompt: "Quels sont les rendez-vous d'aujourd'hui ?", color: '#1e40af', bg: '#eff6ff' },
    { icon: faPlus, label: 'Créer un produit', prefill: 'Crée le produit ', hint: 'Nom et prix, le reste est facultatif', color: '#1e40af', bg: '#eff6ff' },
];

const formatDhs = (v) => v == null ? null
    : `${new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} dhs`;

// A product the assistant prepared (create_product tool): shown for review, created only when
// the pharmacist clicks "Créer le produit".
const ProductDraftCard = ({ action, onConfirm, onCancel }) => {
    const p = action.product || {};
    const rows = [
        ['Code-barres', p.barcode],
        ['Prix public', formatDhs(p.publicPrice)],
        ['Prix hospitalier', formatDhs(p.hospitalPrice)],
        ['Présentation', p.presentation],
        ['Dosage', p.dosage],
        ['Composition (DCI)', p.composition],
        ['Laboratoire', p.laboratoryName],
        ['Classe thérapeutique', p.family],
        ['Type', p.productType],
        ['Remboursable', p.refundable == null ? null : (p.refundable ? 'Oui' : 'Non')],
    ].filter(([, v]) => v != null && v !== '');

    return (
        <div className={`assistant-action assistant-action--${action.state}`}>
            <div className="assistant-action__head">
                <span className="assistant-action__icon"><FontAwesomeIcon icon={faBoxOpen} /></span>
                <div>
                    <span className="assistant-action__label">Nouveau produit</span>
                    <strong className="assistant-action__title">{p.name}</strong>
                </div>
            </div>
            <dl className="assistant-action__rows">
                {rows.map(([label, value]) => (
                    <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
            </dl>

            {p.similarProducts?.length > 0 && (
                <div className="assistant-action__similar">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <div>
                        <strong>Produits au nom proche déjà existants :</strong>
                        <ul>{p.similarProducts.map(n => <li key={n}>{n}</li>)}</ul>
                    </div>
                </div>
            )}

            {action.error && <div className="assistant-action__error">{action.error}</div>}

            {(action.state === 'pending' || action.state === 'creating') && (
                <div className="assistant-action__buttons">
                    <button type="button" className="assistant-action__cancel" onClick={onCancel} disabled={action.state === 'creating'}>
                        Annuler
                    </button>
                    <button type="button" className="assistant-action__confirm" onClick={onConfirm} disabled={action.state === 'creating'}>
                        {action.state === 'creating'
                            ? <span className="spinner-border spinner-border-sm" />
                            : <FontAwesomeIcon icon={faCheck} />}
                        Créer le produit
                    </button>
                </div>
            )}
            {action.state === 'created' && (
                <div className="assistant-action__done">
                    <FontAwesomeIcon icon={faCircleCheck} /> Produit créé
                    <NavLink to={`/products/${action.productId}`}>
                        Voir la fiche <FontAwesomeIcon icon={faArrowRight} />
                    </NavLink>
                </div>
            )}
            {action.state === 'cancelled' && (
                <div className="assistant-action__cancelled">Création annulée</div>
            )}
        </div>
    );
};

export default function Assistant() {
    const { user, settings } = useAuth();
    const enabled = isAssistantEnabled(settings);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [error, setError] = useState(null);

    const textareaRef = useRef();
    const messagesEndRef = useRef();
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);

    const openModal = () => setIsOpen(true);
    const closeModal = () => {
        if (isRecording) stopRecording();
        voiceStopRef.current?.();
        setIsOpen(false);
    };
    const voiceStopRef = useRef(null);

    // Opened from elsewhere in the app (e.g. the sidebar's "Assistant Saydalani" menu item) —
    // this component owns its own open/close state and isn't otherwise reachable from there.
    useEffect(() => {
        eventBus.on('openAssistant', openModal);
        return () => eventBus.off('openAssistant', openModal);
    }, []);

    useEffect(() => {
        if (isOpen) setTimeout(() => textareaRef.current?.focus(), 100);
    }, [isOpen]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isSending]);

    // latest messages for the voice loop, whose callbacks outlive a render
    const messagesRef = useRef(messages);
    useEffect(() => { messagesRef.current = messages; }, [messages]);

    // Resolves with the assistant's reply text (read aloud in voice mode), or null on failure.
    // voice: the message comes from the live voice conversation → short spoken-style reply.
    const sendMessage = async (text, { voice = false } = {}) => {
        const content = text.trim();
        if (!content || isSending) return null;

        setError(null);
        const userMessage = { role: 'user', content };
        const nextMessages = [...messagesRef.current, userMessage];
        messagesRef.current = nextMessages;
        setMessages(nextMessages);
        setInput('');
        setIsSending(true);

        try {
            const history = nextMessages
                .slice(-MAX_HISTORY - 1, -1)
                .filter(m => m.content)
                .map(({ role, content }) => ({ role, content }));

            const res = await axios.post(
                `${API_BASE_URL}/api/v1/assistant/chat`,
                { message: content, history, voice },
                { withCredentials: true }
            );

            // `action`: something the assistant prepared that the pharmacist must confirm
            // (CREATE_PRODUCT → a product card with "Créer le produit")
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: res.data.reply,
                action: res.data.action ? { ...res.data.action, state: 'pending' } : null,
            }]);
            return res.data.reply;
        } catch (err) {
            setError("L'assistant n'a pas pu répondre. Réessayez.");
            return "Désolé, je n'ai pas pu répondre. Réessayez.";
        } finally {
            setIsSending(false);
        }
    };

    const voice = useVoiceConversation({ onUtterance: (text) => sendMessage(text, { voice: true }) });
    voiceStopRef.current = voice.stop; // closeModal is declared above the hook

    const resetConversation = () => {
        voice.stop();
        setMessages([]);
        messagesRef.current = [];
        setError(null);
        setInput('');
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    // the field grows with its content (up to ~6 lines, then scrolls)
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    }, [input]);

    // A click outside the panel closes it — except on the trigger button (it toggles the panel
    // itself) and during a voice conversation, which a click on the page shouldn't hang up.
    const drawerRef = useRef(null);
    useEffect(() => {
        if (!isOpen || voice.active) return undefined;
        const onDown = (e) => {
            if (drawerRef.current?.contains(e.target)) return;
            if (e.target.closest?.('.assistant-trigger')) return;
            closeModal();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, voice.active]);

    // Escape closes the panel
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const updateAction = (index, patch) => {
        setMessages(prev => prev.map((m, i) => (i === index ? { ...m, action: { ...m.action, ...patch } } : m)));
    };

    // The only place a product actually gets created from the assistant: the pharmacist's click.
    // The outcome is also added as an assistant message so the model sees it in the history.
    const confirmCreateProduct = async (index) => {
        const action = messages[index]?.action;
        if (!action || action.state !== 'pending') return;
        updateAction(index, { state: 'creating', error: null });
        try {
            const res = await axios.post(
                `${API_BASE_URL}/api/v1/assistant/actions/create-product`,
                action.product,
                { withCredentials: true }
            );
            updateAction(index, { state: 'created', productId: res.data.id });
            setMessages(prev => [...prev, { role: 'assistant', content: `Le produit « ${res.data.name} » a été créé.` }]);
        } catch (err) {
            const msg = err.response?.status === 403
                ? "Vous n'avez pas la permission de créer des produits."
                : err.response?.data?.message || 'La création du produit a échoué.';
            updateAction(index, { state: 'pending', error: msg });
        }
    };

    const cancelCreateProduct = (index) => {
        updateAction(index, { state: 'cancelled' });
        setMessages(prev => [...prev, { role: 'assistant', content: 'Création du produit annulée.' }]);
    };

    const startRecording = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            audioChunksRef.current = [];

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mediaRecorder.onstop = handleRecordingStop;

            mediaRecorder.start();
            setIsRecording(true);
        } catch (err) {
            setError("Impossible d'accéder au micro.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        streamRef.current?.getTracks().forEach(t => t.stop());
        setIsRecording(false);
    };

    const handleRecordingStop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorderRef.current.mimeType });
        if (blob.size === 0) return;

        setIsTranscribing(true);
        setError(null);
        try {
            const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
            const formData = new FormData();
            formData.append('audio', blob, `voice.${ext}`);
            formData.append('model', 'medium');
            // Skips whisper's language-detection pass (~1-2s) — worth it since the assistant
            // is used in French; trade-off is weaker accuracy if someone dictates in darija/arabe.
            formData.append('language', 'fr');

            const res = await axios.post(`${WHISPER_API_URL}/transcribe`, formData);
            const transcribed = (res.data.text || '').trim();
            if (transcribed) {
                setInput(prev => (prev ? `${prev} ${transcribed}` : transcribed));
                setTimeout(() => textareaRef.current?.focus(), 0);
            }
        } catch (err) {
            setError("Impossible de contacter le service de transcription vocale (whisper).");
        } finally {
            setIsTranscribing(false);
        }
    };

    const toggleRecording = () => {
        if (isRecording) stopRecording();
        else startRecording();
    };

    const handleKeyDownInput = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    // "Saydalani AI" turned off in the settings: close the panel / hang up, render nothing
    useEffect(() => {
        if (!enabled) {
            voice.stop();
            setIsOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    if (!enabled) return null;

    return (
        <>
            <button
                type="button"
                className="assistant-trigger"
                onClick={isOpen ? closeModal : openModal}
                data-tooltip-id="tooltip"
                data-tooltip-content="Assistant IA"
            >
                <FontAwesomeIcon icon={faWandMagicSparkles} />
            </button>

            {/* Right-side panel rather than a centered modal: the page stays visible and usable
                (ask about the list you're looking at), the conversation gets the full height. */}
            <aside
                ref={drawerRef}
                className={`assistant-drawer${isOpen ? ' is-open' : ''}`}
                aria-label="Assistant Saydalani"
                aria-hidden={!isOpen}
            >
                <header className="assistant-drawer__header">
                    <span className="assistant-drawer__icon"><FontAwesomeIcon icon={faWandMagicSparkles} /></span>
                    <span className="assistant-drawer__title">Assistant Saydalani</span>
                    {messages.length > 0 && (
                        <button
                            type="button"
                            className="assistant-drawer__btn"
                            onClick={resetConversation}
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Nouvelle conversation"
                            aria-label="Nouvelle conversation"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                    )}
                    <button type="button" className="assistant-drawer__btn" onClick={closeModal} aria-label="Fermer">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </header>

                <div className="assistant-drawer__body">
                    <div className="assistant-messages">
                        {messages.length === 0 && (
                            <div className="assistant-welcome">
                                <span className="assistant-welcome__icon">
                                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                                </span>
                                <h3 className="assistant-welcome__title">
                                    Bonjour{user?.firstName ? ` ${user.firstName}` : ''} 👋<br />Comment puis-je vous aider ?
                                </h3>
                                <p className="assistant-welcome__sub">
                                    Stock, ventes, patients, péremptions… Posez votre question ou choisissez une suggestion.
                                </p>

                                <div className="assistant-welcome__grid">
                                    {SUGGESTIONS.map(s => (
                                        <button
                                            key={s.label}
                                            type="button"
                                            className="assistant-suggestion"
                                            style={{ '--accent': s.color, '--accent-bg': s.bg }}
                                            onClick={() => {
                                                if (s.prefill) {
                                                    setInput(s.prefill);
                                                    setTimeout(() => {
                                                        const ta = textareaRef.current;
                                                        if (ta) { ta.focus(); ta.setSelectionRange(s.prefill.length, s.prefill.length); }
                                                    }, 0);
                                                } else {
                                                    sendMessage(s.prompt);
                                                }
                                            }}
                                        >
                                            <span className="assistant-suggestion__icon"><FontAwesomeIcon icon={s.icon} /></span>
                                            <span className="assistant-suggestion__text">
                                                <strong>{s.label}</strong>
                                                <span>{s.prompt || s.hint}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {voice.supported && (
                                    <p className="assistant-welcome__voice">
                                        <FontAwesomeIcon icon={faMicrophone} /> Touchez le micro pour lui parler à voix haute.
                                    </p>
                                )}
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <React.Fragment key={i}>
                                {m.content && (
                                    <div className={`assistant-message assistant-message--${m.role}`}>
                                        {m.content}
                                    </div>
                                )}
                                {m.action?.type === 'CREATE_PRODUCT' && (
                                    <ProductDraftCard
                                        action={m.action}
                                        onConfirm={() => confirmCreateProduct(i)}
                                        onCancel={() => cancelCreateProduct(i)}
                                        />
                                )}
                            </React.Fragment>
                        ))}
                        {isSending && (
                            <div className="assistant-message assistant-message--assistant assistant-message--typing">
                                <span /><span /><span />
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {(error || voice.error) && <div className="assistant-error">{voice.error || error}</div>}
                </div>

                <footer className="assistant-drawer__footer">
                    {voice.active ? (
                        // live voice conversation: listen → think → speak → listen...
                        <div className={`assistant-voice assistant-voice--${voice.phase}`}>
                            <button
                                type="button"
                                className="assistant-voice__orb"
                                style={{ '--level': voice.level }}
                                onClick={voice.interrupt}
                                aria-label={voice.phase === 'speaking' ? "Interrompre l'assistant" : 'Envoyer maintenant'}
                                disabled={voice.phase === 'thinking'}
                            >
                                <span className="assistant-voice__ring" />
                                <FontAwesomeIcon icon={voice.phase === 'speaking' ? faVolumeHigh : faMicrophone} />
                            </button>
                            <div className="assistant-voice__body">
                                <span className="assistant-voice__status">
                                    {voice.phase === 'listening' && 'Je vous écoute…'}
                                    {voice.phase === 'thinking' && 'Réflexion…'}
                                    {voice.phase === 'speaking' && "L'assistant parle — touchez pour l'interrompre"}
                                </span>
                                <span className="assistant-voice__interim">
                                    {voice.phase === 'listening' && (voice.interim || 'Parlez naturellement, je réponds à voix haute.')}
                                    {voice.phase === 'thinking' && voice.interim && `« ${voice.interim} »`}
                                </span>
                            </div>
                            <button type="button" className="assistant-voice__end" onClick={voice.stop}>
                                <FontAwesomeIcon icon={faPhoneSlash} /> Terminer
                            </button>
                        </div>
                    ) : (
                        // one large field, the voice + send buttons inside it (bottom right)
                        <div className="assistant-composer" onClick={() => textareaRef.current?.focus()}>
                            <textarea
                                ref={textareaRef}
                                rows={2}
                                placeholder={isRecording ? 'Enregistrement en cours...' : 'Écrivez votre message...'}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDownInput}
                                disabled={isRecording}
                            />
                            <div className="assistant-composer__actions">
                                <button
                                    type="button"
                                    className="assistant-mic-btn"
                                    onClick={(e) => { e.stopPropagation(); voice.start(); }}
                                    disabled={!voice.supported || isSending}
                                    data-tooltip-id="tooltip"
                                    data-tooltip-content={voice.supported ? 'Conversation vocale' : UNSUPPORTED_VOICE_MESSAGE}
                                    aria-label="Conversation vocale"
                                >
                                    <FontAwesomeIcon icon={faMicrophone} />
                                </button>
                                <button
                                    type="button"
                                    className="assistant-send-btn"
                                    onClick={(e) => { e.stopPropagation(); sendMessage(input); }}
                                    disabled={!input.trim() || isSending}
                                    aria-label="Envoyer"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                </button>
                            </div>
                        </div>
                    )}
                </footer>
            </aside>
        </>
    );
}
